import { type ReactNode, useMemo } from 'react';
import Link from 'next/link';

import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  Heading,
  Icon,
  List,
  Modal,
  Paragraph,
  Progress,
  Text,
  TextButton,
  Upsell,
} from '@zillow/constellation-10';
import {
  IconBlueprintFilled,
  IconLaundryFilled,
  IconPanorama360Filled,
  IconPlayCircleFilled,
} from '@zillow/constellation-icons';

import { useGetListingRecommendations } from '../../hooks/queries/properties';
import { ROUTES } from '../../constants/urls';

import type { ListingCompDto } from '@zg-rentals/ts-rental-manager-api-client';

type ListingQualityModalProps = {
  onClose: () => void;
  topComps: Array<ListingCompDto>;
  listingAlias: string;
};

const topOpportunities = [
  { title: 'Video', body: '4 of 5 closest competitors offer this.', icon: <IconPlayCircleFilled /> },
  { title: '3D tour', body: '4 of 5 closest competitors offer this.', icon: <IconPanorama360Filled /> },
  { title: 'Floor plan photos', body: '3 of 5 closest competitors offer this.', icon: <IconBlueprintFilled /> },
  { title: 'Laundry information', body: '2 of 5 closest competitors offer this.', icon: <IconLaundryFilled /> },
];

const OpportunityUpsell = ({ title, body, icon }: { title: string; body: string; icon: ReactNode }) => (
  <Upsell
    closeable={false}
    elevated={false}
    tone="neutral"
    media={<Icon size="lg">{icon}</Icon>}
    header={<Text textStyle="body-sm-bold">{title}</Text>}
    body={<Text textStyle="body-sm">{body}</Text>}
    action={
      <TextButton tone="brand" asChild>
        <Link href={{ pathname: ROUTES.LISTING, query: {} }}>Add to listing</Link>
      </TextButton>
    }
  />
);

const ListingQualityModal = ({ onClose, topComps, listingAlias }: ListingQualityModalProps) => {
  const { recommendationsData } = useGetListingRecommendations({ listingAlias });
  const leadImpact = recommendationsData?.completeness?.leadImpact;

  const allEntries = useMemo(
    () =>
      [...topComps]
        .sort((a, b) => b.listingQualityScore - a.listingQualityScore)
        .map((comp) => ({
          ...comp,
          isCurrentListing: String(comp.listingAlias) === String(listingAlias),
          displayName: comp.propertyName || comp.address,
        })),
    [topComps, listingAlias],
  );

  return (
    <Modal.Root open={true} onOpenChange={(o) => !o && onClose()} size="md">
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                Listing quality
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '800' }}>
                <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
                  <Paragraph>See how your listing compares to similar properties based on listing quality.</Paragraph>
                  <List.Root appearance="number">
                    {allEntries.map((property) => (
                      <List.Item
                        key={property.listingAlias}
                        css={property.isCurrentListing ? { fontWeight: 'bold' } : undefined}
                      >
                        <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
                          <Box css={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '300' }}>
                            <Text textStyle={property.isCurrentListing ? 'body-sm-bold' : 'body-sm'}>
                              {property.displayName}
                            </Text>
                            <Text textStyle={property.isCurrentListing ? 'body-sm-bold' : 'body-sm'}>
                              {Math.round(property.listingQualityScore)}%
                            </Text>
                          </Box>
                          <Progress.Root
                            aria-label={`${property.displayName} listing quality`}
                            size="sm"
                            minValue={0}
                            maxValue={100}
                            value={property.listingQualityScore}
                          >
                            <Progress.Bar>
                              <Progress.Range
                                css={{
                                  backgroundColor:
                                    property.listingQualityScore >= 70
                                      ? 'text.onImpact.feedback.success.hero'
                                      : property.listingQualityScore >= 50
                                        ? 'text.onImpact.feedback.warning.hero'
                                        : 'text.onImpact.feedback.critical.hero',
                                }}
                              />
                              <Progress.Track />
                            </Progress.Bar>
                          </Progress.Root>
                        </Box>
                      </List.Item>
                    ))}
                  </List.Root>
                </Box>
                <Card elevated={false} outlined={false} tone="soft">
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
                    <Text textStyle="body-bold">Top opportunities</Text>
                    <Paragraph>
                      {leadImpact
                        ? `Adding missing details may help increase your leads by up to ${leadImpact}% and improve visibility in filtered renter searches.`
                        : 'Add missing details to improve completeness to help prevent negative impacts to your leads and keep your listings visible in more filtered renter searches.'}
                    </Paragraph>
                    <Box
                      css={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))',
                        gap: '300',
                      }}
                    >
                      {topOpportunities.map((opportunity) => (
                        <OpportunityUpsell key={opportunity.title} {...opportunity} />
                      ))}
                    </Box>
                  </Box>
                </Card>
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Listing quality actions">
                <Modal.Close>
                  <Button tone="brand" emphasis="bare">
                    Close
                  </Button>
                </Modal.Close>
                <Button tone="brand" emphasis="filled" asChild>
                  <Link href={{ pathname: ROUTES.PREMIUM_ANALYTICS, query: { listingAlias } }}>
                    View competitor insights
                  </Link>
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default ListingQualityModal;
